import type { Ref } from 'vue'
import type { ChatCorner } from '~/composables/usePanelLayout'

/**
 * Dragging the floating chat between the four corners of the app shell (MAR-125).
 *
 * ## Why corners and not free positioning
 *
 * The popup sits over the content, so where it rests matters — but an arbitrary x/y is worse than it
 * sounds: it can end up half off a narrower viewport, it has to be re-clamped on every resize, and a
 * user who nudged it 12px has to find it again. Four corners are re-findable, always fully on screen,
 * and persist as one enum rather than coordinates that only made sense at one window size.
 *
 * Dragging is therefore free-form *while the pointer is down* and snaps on release. The drag gives
 * direct manipulation; the snap gives a state worth saving.
 *
 * ## Pointer events, not mouse/touch
 *
 * One code path for mouse, trackpad, touch and pen. `setPointerCapture` is what makes a drag survive
 * the pointer leaving the panel — without it, moving faster than the element follows drops the drag,
 * which is exactly what happens when someone flicks it across the shell.
 */

const HANDLE_SELECTOR = '[data-chat-drag-handle]'
/**
 * Pointer travel, in px, before a press becomes a drag.
 *
 * Without it the FAB is undraggable in practice: it is a button, so every press is a potential click,
 * and treating pointerdown as an immediate grab would swallow the click that opens the chat. The
 * threshold is what lets one gesture be both — press and release is a click, press and move is a drag.
 */
const DRAG_THRESHOLD = 4
/**
 * Controls a press can NEVER be read as a grab (AlexAmin/marcus#362).
 *
 * This used to be every control — `button, a, input, textarea, select, form, [role="button"]` — and
 * a press on any of them was refused outright, with no fallback. On the welcome screen that never
 * bit, because the band's centre is empty. Once a conversation is active the left-hand group
 * ("Nuevo chat" plus the title dropdown trigger, `truncate max-w-[180px]`) reaches past the centre
 * of a ~410px band, so the popup became undraggable at exactly the point a user reaches for the
 * window to move it. Widening the band or shifting the controls only moves the dead zone.
 *
 * Buttons and links are therefore draggable now, and keep their click through the same
 * suppression-after-drag the FAB already uses (`consumeDrag`, applied by `onClickCapture`).
 *
 * Text fields are the exception that stays, and it is not squeamishness: inside an input a
 * press-and-move IS a gesture — the caret selecting text — so the two are genuinely
 * indistinguishable and there is no threshold that separates them. The inline rename field lives in
 * this band, so this is a real press.
 */
const TEXT_ENTRY_SELECTOR = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
/** Matches the `sm:` inset used by the corner classes below. */
export const INSET = 20
/** Tailwind's `sm`. Below it the chat is a full-width pane and dragging is meaningless. */
const DESKTOP = '(min-width: 640px)'

const CORNER_CLASS: Record<ChatCorner, string> = {
  'top-left': 'sm:top-5 sm:left-5',
  'top-right': 'sm:top-5 sm:right-5',
  'bottom-left': 'sm:bottom-5 sm:left-5',
  'bottom-right': 'sm:bottom-5 sm:right-5',
}

/**
 * Which corner a panel belongs to, by its CENTRE rather than the pointer.
 *
 * The pointer is wherever the user happened to grab the header — a panel grabbed near its right edge
 * and dropped on the left half would snap right if we asked the pointer. The centre is the only point
 * that describes where the panel actually is.
 *
 * Pure and exported so the rule is testable without synthesising a pointer gesture.
 */
export function cornerFor(
  panel: { left: number; top: number; width: number; height: number },
  bounds: { left: number; top: number; width: number; height: number },
): ChatCorner {
  const centreX = panel.left + panel.width / 2 - bounds.left
  const centreY = panel.top + panel.height / 2 - bounds.top
  const vertical = centreY < bounds.height / 2 ? 'top' : 'bottom'
  const horizontal = centreX < bounds.width / 2 ? 'left' : 'right'
  return `${vertical}-${horizontal}` as ChatCorner
}

/**
 * ── THE POPUP'S SIZE IS PART OF THE DOCK, NOT A LOOSE STYLE (#351) ────────────────────────────
 *
 * The floating chat is documented as a popup "arrastrable entre las cuatro esquinas del shell"
 * (Notion, `Superficie del Chat (cliente)` §1). That promise is only kept while the panel is small
 * enough for four corners to BE four places. Sized against a fixed pixel cap alone it was not: on a
 * 1280x800 screen the working region is 744px tall and the panel was `min(680px, 100% - 2.5rem)` =
 * 680px, so `top-right` and `bottom-right` differed by 24px of travel, and every right-hand corner
 * parked the panel's 44px drag band across the content column's first row — the toolbar. That is
 * AlexAmin/marcus#351: not a pointer-events bug (the handle claims exactly the 44px it paints) but
 * an opaque panel legitimately covering the controls underneath it, with nowhere to move it to.
 *
 * So the height is a RATIO of the region with a pixel ceiling, rather than a ceiling alone. The
 * ratio is what keeps a short viewport in corner-popup shape; the ceiling is what stops a tall one
 * growing a 900px chat nobody asked for. `PANEL_HEIGHT_RATIO` binds only below roughly a 963px-tall
 * viewport — at 1080p and above the 680px cap still wins and nothing about the panel changes. The
 * cost, accepted deliberately: ~120px of chat height at 1280x800, and a user who parks the panel at
 * `top-right` still covers that row — but now has three other corners that do not.
 *
 * The third term (`100% - 2 * INSET`) never binds at a realistic size; it is there so the insets
 * survive a region shorter than 160px rather than the panel silently overflowing it.
 *
 * These live here, next to `CORNER_CLASS`, because they are the same fact as the corners: where the
 * panel can rest is a function of how big it is. They are also the SOURCE of the CSS the container
 * ships — `PANEL_HEIGHT_CSS` is built from them and bound as a custom property, so the number these
 * tests pin and the number the browser lays out cannot drift apart.
 */
export const PANEL_MAX_HEIGHT = 680
export const PANEL_HEIGHT_RATIO = 0.75

/** The popup's height, in px, inside a working region `regionHeight` tall. */
export function floatingPanelHeight(regionHeight: number): number {
  return Math.min(PANEL_MAX_HEIGHT, regionHeight * PANEL_HEIGHT_RATIO, regionHeight - INSET * 2)
}

/** The same rule as a CSS value, for the container's `sm:h-(--chat-panel-h)`. */
export const PANEL_HEIGHT_CSS =
  `min(${PANEL_MAX_HEIGHT}px, ${PANEL_HEIGHT_RATIO * 100}%, calc(100% - ${INSET * 2}px))`

/**
 * The popup's width, for the container's `sm:w-(--chat-panel-w)`. `32vw` is the viewport, not the
 * region — nothing about the corners depends on it, but a page that keeps its controls clear of the
 * panel does, so it lives here once rather than as a Tailwind literal and a second copy.
 */
export const PANEL_WIDTH_CSS = 'min(420px, 32vw)'

/**
 * How much of a content column's edge the OPEN popup claims, measured from the working area's edge:
 * the panel, its inset, and one more inset of air so a control does not sit flush against its shadow.
 *
 * For a page whose controls run to the edge of the column and must stay reachable while the chat is
 * open — the settings shell (MAR-933 §15), which reserves it on the side the panel is docked to. It
 * is the same move as `RunActionBar.vue`'s launcher clearance, sized for the panel instead of the FAB.
 */
export const PANEL_CLEARANCE_CSS = `calc(${PANEL_WIDTH_CSS} + ${INSET * 2}px)`

/**
 * Where a given corner puts the panel, in region coordinates — the numeric twin of `CORNER_CLASS`.
 *
 * Exported so "the four corners are four distinct positions" is a claim about numbers rather than
 * about class strings. Asserting the spelling of `sm:bottom-5` would pass just as happily with a
 * panel too tall to move.
 */
export function cornerPosition(
  corner: ChatCorner,
  panel: { width: number; height: number },
  region: { width: number; height: number },
): { left: number; top: number } {
  return {
    left: corner.endsWith('left') ? INSET : region.width - panel.width - INSET,
    top: corner.startsWith('top') ? INSET : region.height - panel.height - INSET,
  }
}

/**
 * @param opts.selfHandle the element IS the handle, rather than containing one.
 *   The chat panel is dragged by its header, so a press on a button inside that header must stay a
 *   button press. The FAB has no header — it is the whole target — so that rejection would make it
 *   permanently undraggable. Hence the flag rather than a shared guess.
 */
export function useChatDock(
  bounds: Ref<HTMLElement | null>,
  panel: Ref<HTMLElement | null>,
  opts: { selfHandle?: boolean } = {},
) {
  const { chatCorner, setChatCorner } = usePanelLayout()

  const dragging = ref(false)
  /** Live position while dragging; null means "let the corner classes place it". */
  const dragPosition = ref<{ left: number; top: number } | null>(null)

  let pointerId: number | null = null
  let grabOffset = { x: 0, y: 0 }
  /** Pointer is down and a drag is possible, but the threshold has not been crossed yet. */
  let armed = false
  let pressOrigin = { x: 0, y: 0 }
  let origin = { left: 0, top: 0 }
  /** Set when a press ended as a drag, so the click it would otherwise fire can be ignored. */
  let dragged = false
  /** Whether this gesture took pointer capture — only a drag does, so only a drag releases it. */
  let captured = false

  const cornerClass = computed(() => CORNER_CLASS[chatCorner.value])

  /**
   * Inline placement during a drag. `right`/`bottom` are cleared explicitly: the corner classes may
   * have set them, and a stale `right` next to a live `left` stretches the panel instead of moving it.
   */
  const dragStyle = computed(() => dragPosition.value
    ? {
        left: `${dragPosition.value.left}px`,
        top: `${dragPosition.value.top}px`,
        right: 'auto',
        bottom: 'auto',
      }
    : undefined)

  function clamp(value: number, min: number, max: number) {
    return Math.min(Math.max(value, min), max)
  }

  function onPointerDown(e: PointerEvent) {
    // `typeof window` rather than `import.meta.client`: the guard exists for the `window.matchMedia`
    // call below, and this is the same protection without a Nuxt build constant — which is inlined
    // as `false` outside a Nuxt build and so made the whole drag impossible to drive from a test.
    if (typeof window === 'undefined' || e.button !== 0) return
    if (!window.matchMedia(DESKTOP).matches) return

    const target = e.target as HTMLElement | null
    if (!target?.closest(HANDLE_SELECTOR)) return
    // The FAB has no header — it IS the target — so it has no controls of its own to exempt.
    if (!opts.selfHandle && target.closest(TEXT_ENTRY_SELECTOR)) return

    const panelRect = panel.value?.getBoundingClientRect()
    const boundsRect = bounds.value?.getBoundingClientRect()
    if (!panelRect || !boundsRect) return

    // Armed, not dragging: nothing moves and no click is suppressed until the pointer travels
    // DRAG_THRESHOLD. `preventDefault` is deliberately NOT called here — doing so would kill the
    // click on a press that turns out to be a tap.
    //
    // NOR is the pointer captured here, and that is load-bearing now that a press may land on a
    // button (#362): capture RETARGETS the compatibility click to the capturing element. Measured
    // in Chromium against this exact shape — an ancestor capturing on pointerdown, a button pressed
    // inside it, no travel at all — the click is dispatched at the ancestor and the button's own
    // handler never runs. So capturing here would have made every control in the band dead on
    // arrival: the press would no longer be refused, and the button would no longer work either.
    // It was invisible before only because the one element that captured on pointerdown was the
    // FAB, which captures on itself.
    //
    // Capture is taken in `onPointerMove` instead, at the moment the press becomes a drag. That is
    // the only thing it was ever for — keeping a drag alive once the pointer outruns the panel —
    // and inside DRAG_THRESHOLD the pointer has by definition not gone anywhere.
    armed = true
    dragged = false
    pressOrigin = { x: e.clientX, y: e.clientY }
    grabOffset = { x: e.clientX - panelRect.left, y: e.clientY - panelRect.top }
    origin = { left: panelRect.left - boundsRect.left, top: panelRect.top - boundsRect.top }
    pointerId = e.pointerId
  }

  function onPointerMove(e: PointerEvent) {
    if (!armed || e.pointerId !== pointerId) return

    if (!dragging.value) {
      const travelled = Math.hypot(e.clientX - pressOrigin.x, e.clientY - pressOrigin.y)
      if (travelled < DRAG_THRESHOLD) return
      dragging.value = true
      dragged = true
      dragPosition.value = origin
      // Here, not on pointerdown — see the note there. Without it the drag dies the moment the
      // pointer outruns the panel, which is exactly what flicking it across the shell does.
      const host = e.currentTarget as HTMLElement | null
      if (host?.setPointerCapture) {
        host.setPointerCapture(e.pointerId)
        captured = true
      }
    }

    const panelRect = panel.value?.getBoundingClientRect()
    const boundsRect = bounds.value?.getBoundingClientRect()
    if (!panelRect || !boundsRect) return

    dragPosition.value = {
      left: clamp(e.clientX - boundsRect.left - grabOffset.x, INSET, boundsRect.width - panelRect.width - INSET),
      top: clamp(e.clientY - boundsRect.top - grabOffset.y, INSET, boundsRect.height - panelRect.height - INSET),
    }
  }

  function onPointerUp(e: PointerEvent) {
    if (!armed || e.pointerId !== pointerId) return

    if (dragging.value) {
      const panelRect = panel.value?.getBoundingClientRect()
      const boundsRect = bounds.value?.getBoundingClientRect()
      // WHERE THE DRAG ENDED, NOT WHERE THE PANEL IS PAINTED (AlexAmin/marcus#299).
      //
      // `dragPosition` is set synchronously on every pointermove; it reaches the panel as an inline
      // `left`/`top` through Vue's binding, which is a render and therefore asynchronous. The rect
      // trails the state by however long the renderer takes, so asking the rect here answers for the
      // last frame the browser managed to draw. A release that beats that frame then commits the
      // corner the popup came FROM — `setChatCorner` sees no change and the drag silently does
      // nothing — and a release one frame behind commits a corner the pointer merely crossed.
      //
      // Only the position is stale; the panel's SIZE is not moved by a pending drag, so the rect is
      // still the right place to read width and height from.
      const dropped = dragPosition.value
      if (panelRect && boundsRect && dropped) {
        setChatCorner(cornerFor({
          left: boundsRect.left + dropped.left,
          top: boundsRect.top + dropped.top,
          width: panelRect.width,
          height: panelRect.height,
        }, boundsRect))
      }
    }

    armed = false
    dragging.value = false
    dragPosition.value = null
    // Guarded on `captured`: a press that stayed a click never took capture, and releasing a pointer
    // that was never captured throws `NotFoundError`.
    if (pointerId !== null && captured) {
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(pointerId)
    }
    captured = false
    pointerId = null
  }

  /**
   * True when the press that just ended was a drag — so a click handler on the same element can bow
   * out. Reading it consumes it: the next press starts clean whether or not anyone asked.
   */
  function consumeDrag(): boolean {
    const was = dragged
    dragged = false
    return was
  }

  /**
   * `consumeDrag` for a handle that contains OTHER people's controls (AlexAmin/marcus#362).
   *
   * The FAB is one button with one click handler, so it can ask `consumeDrag()` itself. The panel's
   * drag band is not: it holds "Nuevo chat", the rail toggle, a back link and a dropdown trigger
   * owned by reka-ui, each with its own handler and one of them not ours to edit. Asking every one
   * of them to call `consumeDrag` would be four copies of the rule plus every control added later
   * silently missing it.
   *
   * So the same flag is read once, in the capture phase, on the element that carries the pointer
   * handlers — bound as `@click.capture` in `PanelLayout`. Capture runs before any of those handlers,
   * so stopping propagation there means the click never reaches the control at all. It is the same
   * mechanism as the FAB's, pushed instead of pulled; there is deliberately not a second one.
   *
   * That it is the CLICK being intercepted is what makes this work for the dropdown too: reka-ui's
   * `DropdownMenuTrigger` toggles on `onClick`, not on pointerdown, so a drag begun on the title
   * never leaves an orphaned menu open behind the moved popup.
   *
   * Scoped to the handle because that is the only place a drag can start: a click anywhere else in
   * the panel is nobody's business of ours, and must not be eaten by a stale flag.
   */
  function onClickCapture(e: MouseEvent) {
    const target = e.target as HTMLElement | null
    if (!target?.closest(HANDLE_SELECTOR)) return
    if (!consumeDrag()) return
    e.stopPropagation()
    e.preventDefault()
  }

  return {
    dragging, dragStyle, cornerClass,
    onPointerDown, onPointerMove, onPointerUp, onClickCapture, consumeDrag,
  }
}
